/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { FunnelStep, CardSelectionValue, IEmailCollectionContent, FooterParagraph, LevelCalculationContent, PlanCalculationContent, PlanProposeContent, PlanWayContent, ProcessingContent, ResultContent as ResultContentType, StepProgress } from '../../types/cards';
import { MultiChoiceTileContent } from './variants/MultiChoiceTileContent';
import { MultiChoiceRowContent } from './variants/MultiChoiceRowContent';
import { SingleChoiceContent } from './variants/SingleChoiceContent';
import { EmailCollectionContent } from './variants/EmailCollectionContent';
import { InformContent } from './variants/InformContent';
import { ExtraInfoContent } from './variants/ExtraInfoContent';
import { LevelCalculationContent as LevelCalculationContentComponent } from './variants/LevelCalculationContent';
import { PlanCalculationContent as PlanCalculationContentComponent } from './variants/PlanCalculationContent';
import { PlanProposeContent as PlanProposeContentComponent } from './variants/PlanProposeContent';
import { PlanWayComponent } from './variants/PlanWayComponent';
import { ProcessingComponent } from './variants/ProcessingComponent';
import ResultContent from './variants/ResultContent';
import { quizService } from '../../api/quizService';
import SubscriptionDetail from './variants/SubscriptionDetail/SubscriptionDetail';
import './Card.css';
import {useNavigate, useParams} from "react-router-dom";
import {useFunnelStore} from "../../store/useFunnelStore";
import { useTranslation } from 'react-i18next';
import {pushInfoToGoogleData} from "../../utils/googleTag";
import {facebookEventFire} from "../../utils/facebookEventFire";


interface CardProps {
    step: FunnelStep;
    version: string;
    onSelect: (selection: CardSelectionValue) => void;
    selectedValues: string[];
    funnelId: string;
}

const Card: React.FC<CardProps> = ({ step, version, onSelect, selectedValues }) => {
    const { addChoice, funnel } = useFunnelStore();
    const [detailedStep, setDetailedStep] = useState<FunnelStep | null>(null);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const {stepIndex } = useParams<{ funnelId: string; stepIndex: string; version: string }>();

    useEffect(() => {
        if(funnel.steps){
            const currentStepIndex = funnel.steps.findIndex((item:any) => item.id === step.id)
            const n = parseInt(currentStepIndex+1 || '0', 10);
            if (!isNaN(n) && n > 0 && n % 5 === 0) {
                pushInfoToGoogleData({
                    event: `complete_${n}_questions`,
                    data: {}
                });
                facebookEventFire(`${n}_QuestionComplete`);
            }
        }

    }, [stepIndex])

    useEffect(() => {
        // Reset detailedStep immediately when step or version changes
        setDetailedStep(null);
        const fetchStepDetails = async () => {
            if (!step.documentId) {
                setError('Missing step identifier');
                return;
            }

            try {
                setError(null);
                const details = await quizService.getFunnelStepDetails(step.documentId, step.stepType);
                if (details) {
                    // Filter stepContent to get the right version
                    const filteredDetails = {
                        ...details,
                        stepContent: details.stepContent?.filter(content => {
                            // Check if content has version property
                            if ('version' in content) {
                                return content.version === version.replace('v', ''); // Remove 'v' prefix for comparison
                            }
                            // If no version property, assume it's version 1
                            return version === 'v1';
                        }) || []
                    };

                    // If no content found for the specific version, fallback to version 1
                    if (filteredDetails.stepContent.length === 0 && version !== 'v1') {
                        filteredDetails.stepContent = details.stepContent?.filter(content => {
                            if ('version' in content) {
                                return content.version === '1';
                            }
                            return true;
                        }) || details.stepContent || [];
                    }

                    setDetailedStep(filteredDetails);
                }
            } catch (err) {
                console.error('Error fetching step details:', err);
                setError('Failed to load step details');
            }
        };

        fetchStepDetails();
    }, [step.documentId, step.stepType, version]);

    const handleSelect = (optionIds?: string | string[]) => {
        if (stepIndex ==='1') {
            pushInfoToGoogleData({
                event: 'quiz_start'
            });
            facebookEventFire('QuizStart');
        }

        const content = detailedStep?.stepContent?.[0];

        // For multi-select, we just update the selected values without moving to next step
        if (step.stepType === 'CHOICE' && content && 'selectionType' in content && content.selectionType === 'MULTIPLE') {
            const selection = {
                stepId: step.id.toString(),
                selectedOptionIds: optionIds ? (Array.isArray(optionIds) ? optionIds : [optionIds]) : [],
                isMultiSelect: true
            };
            onSelect(selection);
            return; // Return early to prevent moving to next step
        }
        const selection = {
            stepId: step.id.toString(),
            selectedOptionIds: optionIds ? (Array.isArray(optionIds) ? optionIds : [optionIds]) : []
        };
        onSelect(selection);
    };

    const handleNextStep = () => {
        // if (content && 'nextStep' in content && content.nextStep) {
            addChoice({
                stepIndex: stepIndex || '',
                content: {
                    stepTitle: step.title,
                    selectedValues
                }
            })
            onSelect({
                stepId: step.id.toString(),
                selectedOptionIds: selectedValues,
                // nextStep: content.nextStep.toString()
            });
        // }
    };

    const renderProgress = (currentStep: FunnelStep) => {
        // Don't show progress for certain step types
        // console.log('progress.funnelStepId', stepProgress)
        if (
            [
                'PROCESSING',
                'RESULT',
                'LEVEL_CALCULATION',
                'EXTRA_INFO',
                'INFO',
                'EMAIL_COLLECTION',
                'SUBSCRIPTION_DETAIL'
            ].includes(currentStep.stepType)
        ) {
            return null;
        }
        // Check if we have funnel data and stepProgress
        if (!funnel || !funnel.stepProgress || !funnel.totalSteps) {
            return null;
        }

        const stepProgress = funnel.stepProgress.find((progress: StepProgress) => progress.funnelStep?.documentId === currentStep?.documentId);

        if (!stepProgress) {
            return null; // No progress record found for this step
        }

        const progressPercentage = (stepProgress.progressIndex / funnel.totalSteps) * 100;

        const n = parseInt(stepProgress.progressIndex || '0', 10);
        if (!isNaN(n) && n > 0 && n % 5 === 0) {
            pushInfoToGoogleData({
                event: `complete_${n}_questions`,
                data: {}
            });
            facebookEventFire(`${n}_QuestionComplete`);
        }

        return (
            <div className="card__progress">
                {t("progressText", { step: stepProgress.progressIndex, totalSteps: funnel.totalSteps})}
                <div className="card__progress-line">
                    <div
                        className="card__progress-line-fill"
                        style={{ width: `${progressPercentage}%` }}
                    />
                </div>
            </div>
        );
    };

    const renderSubTitle = (stepIndex: string) => {
        if(stepIndex === '1' || stepIndex === '2' )
        return <p className="card__quiz_time">{t('quizTitle')}</p>
    }

    const renderContent = (stepIndex: number) => {
        if (!detailedStep || !detailedStep.stepContent?.[0]) return null;

        const content = detailedStep.stepContent[0];

        if (step.stepType === 'SUBSCRIPTION_DETAIL' &&
            content.__component === 'subscription-detail-content.subscription-detail-content') {
            const subscriptionContent = content as any;
            return (
                <SubscriptionDetail
                    step={step}
                    stepIndex={stepIndex}
                    content={subscriptionContent}
                    onCheckout={({selectedPrice, coupon, isSingle, productId}) => {
                        navigate('/checkout', { state: {
                            priceId: selectedPrice.id,
                                productId: productId,
                                couponId: coupon?.id,
                                isSingle } });
                        localStorage.setItem('checkout_info', JSON.stringify({
                            priceId: selectedPrice.id,
                            productId: productId,
                            couponId: coupon?.id,
                            isSingle
                        }))
                        onSelect({
                            stepId: step.id.toString(),
                            selectedOptionIds: [],
                            nextStep: subscriptionContent.nextStep?.toString() || '',
                            selectedPrice: selectedPrice
                        });
                    }}
                />
            );
        }

        if (step.stepType === 'PROCESSING' &&
            content.__component === 'processing-content.processing-content') {
            const processingContent = content as ProcessingContent;
            const testimonials = (processingContent.testimonials || []).map(t => {
                const testimonial = t as { user_name: string; testimonial: string; stars: number; avatar?: any };
                let avatarUrl =
                    testimonial.avatar?.formats?.thumbnail?.url ||
                    testimonial.avatar?.formats?.small?.url ||
                    testimonial.avatar?.url ||
                    'https://ui-avatars.com/api/?name=' + encodeURIComponent(testimonial.user_name || '');

                return {
                    avatar: avatarUrl,
                    name: testimonial.user_name,
                    rating: testimonial.stars,
                    text: testimonial.testimonial,
                };
            });
            return (
                <ProcessingComponent
                    step={step}
                    message={processingContent.message}
                    time={processingContent.time}
                    testimonials={testimonials}
                    onComplete={() => handleSelect()}
                />
            );
        }

        // Handle RESULT step type
        if (step.stepType === 'RESULT' &&
            content.__component === 'result-content.result-content') {
            const resultContent = content as ResultContentType;
            let metrics = resultContent.metrics;
            if (resultContent.resultType === 'GROWTH_PROJECTION' && (resultContent as any).attributes) {
                metrics = (resultContent as any).attributes.map((attr: any) => ({
                    label: attr.title,
                    value: attr.description,
                    icon: attr.icon?.formats?.small?.url || attr.icon?.url || '',
                }));
            }
            return (
                <ResultContent
                    step={step}
                    title={resultContent.title || step.title}
                    description={resultContent.description || step.subtitle}
                    metrics={metrics}
                    resultLevel={resultContent.resultLevel}
                    resultScore={resultContent.resultScore}
                    resultType={resultContent.resultType}
                    targetDate={resultContent.targetDate}
                    buttonText={resultContent.buttonText}
                    scoreLevel={resultContent.scoreLevel}
                    onContinue={() => handleSelect()}
                />
            );
        }

        // Handle PLAN_WAY step type
        if (step.stepType === 'PLAN_WAY' &&
            'weeks' in content &&
            'title' in content) {
            const planContent = content as PlanWayContent;
            return (
                <PlanWayComponent
                    step={step}
                    title={planContent.title}
                    weeks={planContent.weeks}
                    onContinue={() => handleSelect()}
                />
            );
        }

        // Handle PLAN_PROPOSE step type
        if (step.stepType === 'PLAN_PROPOSE' &&
            'progressPoints' in content &&
            'goal' in content &&
            'targetDate' in content &&
            'title' in content &&
            'subtitle' in content) {
            const planContent = content as PlanProposeContent;
            return (
                <PlanProposeContentComponent
                    step={step}
                    title={planContent.title}
                    subtitle={planContent.subtitle}
                    targetDate={planContent.targetDate}
                    goal={planContent.goal}
                    progressPoints={planContent.progressPoints}
                />
            );
        }

        // Handle PLAN_CALCULATION step type
        if (step.stepType === 'PLAN_CALCULATION' &&
            'testimonials' in content &&
            'title' in content &&
            'subtitle' in content) {
            const planContent = content as PlanCalculationContent;
            return (
                <PlanCalculationContentComponent
                    step={step}
                    title={planContent.title}
                    subtitle={planContent.subtitle}
                    testimonials={planContent.testimonials}
                />
            );
        }

        // Handle LEVEL_CALCULATION step type
        if (step.stepType === 'LEVEL_CALCULATION' &&
            'readinessScore' in content &&
            'readinessLabel' in content &&
            'metrics' in content) {
            const levelContent = content as LevelCalculationContent;
            return (
                <LevelCalculationContentComponent
                    step={step}
                    readinessScore={levelContent.readinessScore}
                    readinessLabel={levelContent.readinessLabel}
                    metrics={levelContent.metrics}
                />
            );
        }

        // Handle EXTRA_INFO step type
        if (step.stepType === 'EXTRA_INFO' && content.__component === 'extra-info-content.extra-info-content') {
            const extraInfoContent = content as import('../../types/cards').ExtraInfoContent;
            // Map infoCards to match the InfoCard type expected by ExtraInfoContent
            const infoCards = (extraInfoContent.infoCards || []).map(card => ({
                id: card.id,
                title: card.title,
                description: card.description,
                icon: card.icon
                    ? {
                        url: card.icon.url,
                        formats: {
                            thumbnail: card.icon.formats?.thumbnail && card.icon.formats.thumbnail.url ? { url: card.icon.formats.thumbnail.url } : undefined,
                            small: card.icon.formats?.small && card.icon.formats.small.url ? { url: card.icon.formats.small.url } : undefined
                        }
                    }
                    : undefined
            }));
            return (
                <ExtraInfoContent
                    step={step}
                    title={extraInfoContent.title || step.title}
                    description={extraInfoContent.description || ''}
                    infoCards={infoCards}
                    buttonText={extraInfoContent.buttonText || 'Continue'}
                    mainBanner={extraInfoContent.mainBanner?.url || null}
                    onContinue={() => handleSelect()}
                />
            );
        }

        // Handle INFO step type
        if (step.stepType === 'INFO' && 'mainBanner' in content && content.mainBanner) {
            return (
                <InformContent
                    step={step}
                    image={content.mainBanner}
                    title={step.title}
                    description={content.description || ''}
                    features={[]}
                    stats={[]}
                    buttonText={content.buttonText || null}
                    onContinue={() => handleSelect()}
                />
            );
        }

        // Handle EMAIL_COLLECTION step type
        if (step.stepType === 'EMAIL_COLLECTION' && '__component' in content && content.__component === 'email-collection.email-collection') {
            const emailContent = content as IEmailCollectionContent;
            return (
                <EmailCollectionContent
                    step={step}
                    content={emailContent}
                    stepIndex={stepIndex}
                    onSelect={(email, termsConsent, marketingConsent) => {
                        localStorage.setItem('emailAddress', email);
                        onSelect({
                            stepId: step.id.toString(),
                            selectedOptionIds: [email],
                            email,
                            termsConsent,
                            marketingConsent
                        });
                    }}
                />
            );
        }

        // Handle other step types (TILE, etc.)
        if ('options' in content) {
            const options = content.options.map(option => ({
                id: option.id.toString(),
                key: option.id.toString(),
                value: option.label,
                text: option.label,
                title: option.label,
                emoji: (option as any).emoji || '',
                image: 'image' in option && option.image ? option.image : null
            }));

            switch (content.layoutSettings) {
                case 'TILES':
                    return (
                        <MultiChoiceTileContent
                            step={step}
                            options={options}
                            onSelect={(value) => handleSelect(value)}
                            selectedValues={selectedValues}
                        />
                    );
                case 'ROWS':
                    return content.selectionType === 'MULTIPLE' ? (
                        <div className="card__multi-select">
                            <MultiChoiceRowContent
                                step={step}
                                options={options as import('../../types/cards').QuizOption[]}
                                onSelect={(value) => handleSelect(value)}
                                selectedValues={selectedValues}
                                multiple={true}
                                stepId={step.id}
                                stepIndex={stepIndex}
                                cardTitle={step.title}
                            />
                            {(
                                <div className="card__next-button-fixed">
                                    <button
                                        disabled={selectedValues.length < 1}
                                        className="card__next-button"
                                        onClick={handleNextStep}
                                    >
                                        {t('nextStepText')}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <SingleChoiceContent
                            step={step}
                            options={options as import('../../types/cards').QuizOption[]}
                            onSelect={(value) => handleSelect(value)}
                            selectedValues={selectedValues}
                            stepId={step.id}
                            stepIndex={stepIndex}
                            cardTitle={step.title}
                        />
                    );
                default:
                    return null;
            }
        }

        return null;
    };

    const renderFooter = (footer: FooterParagraph[]) => {
        if (!footer) return null;

        return (
            <div className="card__footer">
                {footer.map((paragraph, paragraphIndex) => (
                    <p key={paragraphIndex} className="card__footer-text">
                        {paragraph.children.map((child, childIndex) => {
                            if (child.type === 'link') {
                                return (
                                    <a
                                        key={childIndex}
                                        href={child.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="card__footer-link"
                                    >
                                       {child.children[0].text}
                                    </a>
                                );
                            }
                            return <span key={childIndex}>{child.text}</span>;
                        })}
                    </p>
                ))}
            </div>
        );
    };

    if (error) {
        return (
            <div className="card card--error">
                <div className="card__header">
                    {step.title && <h2 className="card__title">{step.title}</h2>}
                    {step.subtitle && <h3 className="card__subtitle">{step.subtitle}</h3>}
                </div>
                <div className="card__content">{error}</div>
            </div>
        );
    }

    return (
        <div
            className="container_cards_content bg-white rounded-[18px] shadow-lg p-7 sm:p-8 md:p-10 max-w-[360px] w-full mx-auto sm:rounded-[24px] sm:max-w-[400px] overflow-y-auto overflow-x-hidden"
        >
            {/* Only render content when detailedStep is ready */}
            {detailedStep && detailedStep.stepContent?.[0] ? (
                <>
                    {renderProgress(step)}
                    {step.stepType !== 'INFO' && step.stepType !== 'PROCESSING' && <h1 className="card__title text-xl sm:text-2xl md:text-3xl font-bold mb-2">{step.title}</h1>}
                    {step.subtitle && <p className="card__subtitle text-base sm:text-lg md:text-xl mb-4">{step.subtitle}</p>}
                    {step.stepType !== 'INFO' && renderSubTitle(stepIndex || '')}
                    <div className="card__content">
                        {renderContent(parseInt(stepIndex || '1'))}
                    </div>
                    {detailedStep?.footer && renderFooter(detailedStep?.footer)}
                </>
            ) : (
                // Blank space while loading
                <div style={{ minHeight: '300px' }}></div>
            )}
        </div>
    );
};

export default Card;
